import { Point2D } from '@ahha/stableComponents/Canvas/types/point'
import { CornerAnchors } from '@ahha/stableComponents/Canvas/types/shape'
import { getUnitVector } from '@ahha/stableComponents/Canvas/utils/math'
import { isKeyOf, isNumber } from '@ahha/utils/@types/typeChecks'
import { hexToRgba } from '@ahha/utils/color'
import { warn } from '@ahha/utils/log'
import Konva from 'konva'
import { isNaN } from 'lodash'

const FALLBACK_FILL = '#FFF'

const FREQUENTLY_USED_COLOR_HEX_MAP = {
  white: '#FFF',
  black: '#000',
  gray: '#808080',
  red: '#FF0000',
  green: '#00FF00',
  blue: '#0000FF',
  yellow: '#FFFF00',
  magenta: '#FF00FF',
  cyan: '#00FFFF',
  transparent: '#0000',
}

const ergonomicColorToHex = (color: string) => {
  const lowerCased = color.toLowerCase()

  if (isKeyOf(lowerCased, FREQUENTLY_USED_COLOR_HEX_MAP)) {
    return FREQUENTLY_USED_COLOR_HEX_MAP[lowerCased]
  }
  return color
}

export const getFillColor = (color: string | undefined, opacity: number | undefined) => {
  if (!color) {
    return undefined
  }
  if (!isNumber(opacity) || opacity === 1) {
    return color
  }
  const [r, g, b] = hexToRgba(ergonomicColorToHex(color))

  if (isNaN(r) || isNaN(g) || isNaN(b)) {
    warn(
      'In order to set the opacity of a Shape, "fill" should be a hex value. '
      + `Current "fill" value is ${color}.`
    )
    return FALLBACK_FILL
  }

  return `rgba(${r}, ${g}, ${b}, ${opacity})`
}

/**
 * @param {[number, number][]} points 2D 도형을 이루는 점 [x, y]들의 배열
 * @returns
 */
export const getPolygonBoundingRect = (points: Point2D[]) => {
  const [minX, maxX] = points.reduce(
    ([min, max], [x]) => [Math.min(min, x), Math.max(max, x)],
    [Infinity, -Infinity]
  )
  const [minY, maxY] = points.reduce(
    ([min, max], [, y]) => [Math.min(min, y), Math.max(max, y)],
    [Infinity, -Infinity]
  )

  return {
    minX,
    minY,
    maxX,
    maxY,
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  }
}

/**
 * 폴리곤 위의 한 점에 대해 바깥 방향을 향하는 단위 벡터를 반환.
 * 해당 벡터는 `index`에 위치한 점을 지나는 두 벡터가 이루는 각을 이등분하고, 폴리곤의 외부 영역을 향하는 벡터.
 *  */
export const getPointDirectionVector = (linePoints: Point2D[], index: number) => {
  const L = linePoints.length
  const prevP = linePoints[(index - 1 + L) % L]
  const p = linePoints[index]
  const nextP = linePoints[(index + 1) % L]

  const [x1, y1] = getUnitVector(prevP, p)
  const [x2, y2] = getUnitVector(nextP, p)

  return getUnitVector([0, 0], [(x1 + x2) / 2, (y1 + y2) / 2])
}

/* Snapshot 관련 */
export const getPolygonData = (line: Konva.Line | null) => {
  if (!line) {
    return []
  }
  return line.getAttrs().deflatedPoints as Point2D[]
}

export const getPolygonFixedPoint = (data: Point2D[], fixedAnchor: CornerAnchors): Point2D => {
  const { minX, minY, maxX, maxY } = getPolygonBoundingRect(data)

  if (fixedAnchor === 'tl') {
    return [minX, minY]
  }
  if (fixedAnchor === 'tr') {
    return [maxX, minY]
  }
  if (fixedAnchor === 'bl') {
    return [minX, maxY]
  }
  return [maxX, maxY]
}
